import Add from '@mui/icons-material/Add'
import Delete from '@mui/icons-material/Delete'
import { Button, IconButton, MenuItem, Select, Slider, Stack } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import CROPS from '../mp_backend_crops.json'

type FilterType =
  | 'usda_cdl'
  | 'deepcdl'
  | 'tillage'
  | 'planting_date'
  | 'covercrop'
  | 'harvest_date'

type FilterEntry = {
  id: string
  type: FilterType
  year: number
  landUseCode: number
  tillageCode: number
  plantingDateRange: [number, number]
  coverCropValue?: number // 0 = no, 1 = yes
}

type FiltersProps = {
  setFinalFindSql: (sql: string) => void
}

const FILTER_TYPES: Array<{ label: string; value: FilterType }> = [
  { label: 'usda_cdl', value: 'usda_cdl' },
  { label: 'deepcdl', value: 'deepcdl' },
  { label: 'tillage', value: 'tillage' },
  { label: 'planting_date', value: 'planting_date' },
  { label: 'harvest_date', value: 'harvest_date' },
  { label: 'covercrop', value: 'covercrop' },
]

const COVERCROP_OPTIONS = [
  { label: 'no', value: 0 },
  { label: 'yes', value: 1 },
]

const TILLAGE_OPTIONS = [
  { label: 'no-till', value: 0 },
  { label: 'conservation', value: 1 },
  { label: 'conventional', value: 2 },
]

const YEARS = [2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018]

const LAND_USE_OPTIONS = CROPS.filter(crop => typeof crop.CD_ID === 'number').map(crop => ({
  label: crop.MP_NAME,
  value: crop.CD_ID as number,
}))

function createEntry(): FilterEntry {
  const defaultLandUse = LAND_USE_OPTIONS[0]?.value ?? 1
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: 'usda_cdl',
    year: 2024,
    landUseCode: defaultLandUse,
    tillageCode: 0,
    plantingDateRange: [1, 366],
    coverCropValue: 0,
  }
}

export const Filters = ({ setFinalFindSql }: FiltersProps) => {
  const [entries, setEntries] = useState<FilterEntry[]>([createEntry()])

  const filterParts = useMemo(() => {
    return entries
      .map(entry => {
        if (entry.type === 'usda_cdl') {
          return `usda_cdl_${entry.year} = ${entry.landUseCode}`
        }
        if (entry.type === 'deepcdl') {
          return `deepcdl_${entry.year} = ${entry.landUseCode}`
        }
        if (entry.type === 'tillage') {
          return `tillage_${entry.year} = ${entry.tillageCode}`
        }
        if (entry.type === 'planting_date') {
          const [min, max] = entry.plantingDateRange
          return `(planting_date_${entry.year} >= ${min} AND planting_date_${entry.year} <= ${max})`
        }
        if (entry.type === 'harvest_date') {
          const [min, max] = entry.plantingDateRange
          return `(harvest_date_${entry.year} >= ${min} AND harvest_date_${entry.year} <= ${max})`
        }
        if (entry.type === 'covercrop') {
          return `covercrop_${entry.year} = ${entry.coverCropValue}`
        }
        return ''
      })
      .filter(Boolean)
  }, [entries])

  const finalFilterSql = useMemo(() => filterParts.join(' AND '), [filterParts])

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setFinalFindSql(finalFilterSql)
    }, 300)

    return () => {
      window.clearTimeout(timerId)
    }
  }, [finalFilterSql, setFinalFindSql])

  return (
    <Stack spacing={1} sx={{ p: 1 }}>
      {entries.map(entry => (
        <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
          <Select
            size="small"
            value={entry.type}
            onChange={event => {
              const type = event.target.value as FilterType
              setEntries(current =>
                current.map(item => {
                  if (item.id !== entry.id) return item
                  // Reset type-specific values when changing type
                  if (type === 'covercrop') {
                    return { ...item, type, coverCropValue: 0 }
                  }
                  if (type === 'usda_cdl' || type === 'deepcdl') {
                    return { ...item, type, landUseCode: LAND_USE_OPTIONS[0]?.value ?? 1 }
                  }
                  if (type === 'tillage') {
                    return { ...item, type, tillageCode: 0 }
                  }
                  if (type === 'planting_date' || type === 'harvest_date') {
                    return { ...item, type, plantingDateRange: [1, 366] }
                  }
                  return { ...item, type }
                })
              )
            }}
            sx={{ minWidth: 140, backgroundColor: '#444' }}
          >
            {FILTER_TYPES.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={entry.year}
            onChange={event => {
              const year = Number(event.target.value)
              setEntries(current =>
                current.map(item => (item.id === entry.id ? { ...item, year } : item))
              )
            }}
            sx={{ minWidth: 110, backgroundColor: '#444' }}
          >
            {YEARS.map(year => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>

          {entry.type === 'covercrop' ? (
            <Select
              size="small"
              value={entry.coverCropValue}
              onChange={event => {
                const coverCropValue = Number(event.target.value)
                setEntries(current =>
                  current.map(item => (item.id === entry.id ? { ...item, coverCropValue } : item))
                )
              }}
              sx={{ minWidth: 120, backgroundColor: '#444' }}
            >
              {COVERCROP_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          ) : null}

          {(entry.type === 'usda_cdl' || entry.type === 'deepcdl') && (
            <Select
              size="small"
              value={entry.landUseCode}
              onChange={event => {
                const landUseCode = Number(event.target.value)
                setEntries(current =>
                  current.map(item => (item.id === entry.id ? { ...item, landUseCode } : item))
                )
              }}
              sx={{ minWidth: 220, backgroundColor: '#444' }}
            >
              {LAND_USE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}

          {entry.type === 'tillage' && (
            <Select
              size="small"
              value={entry.tillageCode}
              onChange={event => {
                const tillageCode = Number(event.target.value)
                setEntries(current =>
                  current.map(item => (item.id === entry.id ? { ...item, tillageCode } : item))
                )
              }}
              sx={{ minWidth: 220, backgroundColor: '#444' }}
            >
              {TILLAGE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}

          {(entry.type === 'planting_date' || entry.type === 'harvest_date') && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: 300, zIndex: 1000 }}
            >
              <span
                style={{
                  minWidth: 32,
                  padding: '4px',
                  backgroundColor: '#000a',
                  textAlign: 'center',
                }}
              >
                {entry.plantingDateRange[0]}
              </span>
              <Slider
                value={entry.plantingDateRange}
                min={1}
                max={366}
                step={1}
                onChange={(_, value) => {
                  if (!Array.isArray(value)) {
                    return
                  }
                  setEntries(current =>
                    current.map(item =>
                      item.id === entry.id
                        ? { ...item, plantingDateRange: [value[0], value[1]] }
                        : item
                    )
                  )
                }}
                sx={{ width: 220, px: 1 }}
              />
              <span
                style={{
                  minWidth: 32,
                  padding: '4px',
                  backgroundColor: '#000a',
                  textAlign: 'center',
                }}
              >
                {entry.plantingDateRange[1]}
              </span>
            </Stack>
          )}

          <IconButton
            size="small"
            aria-label="Remove filter"
            onClick={() => {
              setEntries(current =>
                current.length > 1 ? current.filter(item => item.id !== entry.id) : current
              )
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ))}

      <Button
        startIcon={<Add />}
        onClick={() => setEntries(current => [...current, createEntry()])}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add filter
      </Button>
    </Stack>
  )
}
